"use client"

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom"
import Layout from "../components/Layout"
import Card from "../components/Card"
import Table from "../components/Table"
import Button from "../components/Button"
import Alert from "../components/Alert"
import { Search, Eye, Package } from "react-feather"

const ActiveUsers = () => {
  const [activeUsers, setActiveUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [alert, setAlert] = useState(null)
  const [roleFilter, setRoleFilter] = useState("all")
  const navigate = useNavigate()
  const searchTimeout = useRef(null)
  const API_BASE_URL = 'https://api.tenarx.net';

  const fetchActiveUsers = async (page = 1, search = "", role = "all") => {
    try {
      setLoading(true)
      const token = localStorage.getItem("userToken")

      if (!token) {
        navigate("/")
        return
      }

      // Add search and pagination parameters to the URL
      const url = new URL(`${API_BASE_URL}/api/auth/admin/all-active`)
      url.searchParams.append("page", page)
      url.searchParams.append("limit", 10)
      if (search) url.searchParams.append("search", search)
      if (role && role !== "all") url.searchParams.append("role", role)

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch active users: ${response.statusText}`)
      }

      const data = await response.json()
      setActiveUsers(data.data || [])
      setTotalPages(Math.ceil((data.total || 0) / 10))
    } catch (error) {
      setError(error.message)
      setAlert({
        type: "error",
        message: error.message,
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchActiveUsers(currentPage, searchTerm, roleFilter)
    // eslint-disable-next-line
  }, [currentPage, roleFilter, navigate])

  const handleSearch = () => {
    setCurrentPage(1)
    fetchActiveUsers(1, searchTerm, roleFilter)
  }

  const handleSearchInputChange = (e) => {
    const value = e.target.value
    setSearchTerm(value)
    setCurrentPage(1)
    if (searchTimeout.current) clearTimeout(searchTimeout.current)
    searchTimeout.current = setTimeout(() => {
      fetchActiveUsers(1, value, roleFilter)
    }, 400)
  }

  const handleRoleFilterChange = (e) => {
    const value = e.target.value
    setRoleFilter(value)
    setCurrentPage(1)
  }

  const handleViewClick = (userId) => {
    navigate(`/pending-account/${userId}`)
  }

  const columns = [
    {
      header: "No",
      accessor: "",
      render: (row, index) => (currentPage - 1) * 10 + index + 1,
      style: { width: "50px" },
    },
    {
      header: "Email",
      accessor: "email",
    },
    {
      header: "Role",
      accessor: "role",
      render: (row) => (
        <span className="px-2 py-1 text-xs font-semibold rounded-full capitalize bg-blue-100 text-blue-800">
          {row.role}
        </span>
      ),
    },
    {
      header: "Status",
      accessor: "accountStatus",
      render: (row) => (
        <span className="px-2 py-1 text-xs font-semibold rounded-full capitalize bg-green-100 text-green-800">
          {row.accountStatus?.replace("_", " ")}
        </span>
      ),
    },
    {
      header: "Info Submitted",
      accessor: "personalInfoSubmitted",
      render: (row) => <span>{row.personalInfoSubmitted ? "Yes" : "No"}</span>,
    },
    {
  header: "Medicine",
  accessor: "",
  render: (row) =>
    row.role !== "admin" && row.role !== "customer" && (
      <Button
        variant="secondary"
        size="sm"
        onClick={() => navigate(`/medicine/${row._id}`)}
        className="flex items-center gap-1"
      >
        <Package className="w-4 h-4" />
        Medicine
      </Button>
    ),
},

    {
      header: "Actions",
      accessor: "",
      render: (row) => (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => handleViewClick(row._id)}
          className="flex items-center gap-1"
        >
          <Eye className="w-4 h-4" />
          View
        </Button>
      ),
    },
  ]

  return (
    <Layout title="Active Users">
      {alert && (
        <Alert type={alert.type} dismissible onDismiss={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      <Card>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-3 flex-1">
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Search className="w-4 h-4 text-gray-500" />
              </div>
              <input
                type="text"
                placeholder="Search by email..."
                className="pl-10 pr-4 py-2 border rounded-lg w-full"
                value={searchTerm}
                onChange={handleSearchInputChange}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              />
            </div>
            <div className="flex items-center">
              <span className="mr-2 text-gray-600 text-sm">Role</span>
              <select
                className="border rounded-lg px-4 py-2"
                value={roleFilter}
                onChange={handleRoleFilterChange}
              >
                <option value="all">All Roles</option>
                <option value="pharmacy">Pharmacy</option>
                <option value="supplier">Supplier</option>
                <option value="customer">Customer</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>
          <Button onClick={handleSearch}>Search</Button>
        </div>

        <Table columns={columns} data={activeUsers} isLoading={loading} emptyMessage="No active users found" />

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-6">
            <nav className="inline-flex rounded-md shadow">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum
                if (totalPages <= 5) {
                  pageNum = i + 1
                } else if (currentPage <= 3) {
                  pageNum = i + 1
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i
                } else {
                  pageNum = currentPage - 2 + i
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-1 border-t border-b border-gray-300 bg-white text-sm font-medium ${
                      currentPage === pageNum
                        ? "bg-blue-50 text-blue-600 border-blue-500"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {pageNum}
                  </button>
                )
              })}
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </nav>
          </div>
        )}
      </Card>
    </Layout>
  )
}

export default ActiveUsers
